import {
    useMemo, useCallback,
} from 'react'
import {
    useQuery,
    QueryFunction,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query'
import {
    useDispatch,
} from 'react-redux'
import {
    useJWTToken,
} from 'app/Auth'
import {
    TableResponse,
} from 'app/types/request.types'
import {
    RequestKeys,
} from 'app/hooks/useRequest/utils/keys'

export type UseRequestParam<ResponseData = unknown> = {
    key: RequestKeys,
    params?: Record<string, any>,
    requestFunc: (dispatch: (any) => void) => QueryFunction<ResponseData>,
    refetchInterval?: number | false | ((any) => number | false),
    refetchIntervalInBackground?: boolean,
    enabled?: boolean,
    keepPreviousData?: boolean,
    staleTime?: number,
}

export type UseRequestResponse<T> = {
    data: T,
    isDataReady: boolean,
    isFetching: boolean,
    isError: boolean,
    error: any,
    refetch: (options?: {
        throwOnError: boolean,
        cancelRefetch: boolean
    }) => Promise<UseQueryResult<T, unknown>>,
    invalidate: () => void,
}

const useRequest = <Type = TableResponse>({
    key,
    params,
    requestFunc,
    enabled = true,
    refetchInterval = false,
    refetchIntervalInBackground = false,
    keepPreviousData = false,
    staleTime = 1000,
}: UseRequestParam<Type>): UseRequestResponse<Type> => {
    const token = useJWTToken()
    const dispatch = useDispatch()
    const placeholderDataFnToKeepPreviousData = (prev) => {
        return prev
    }

    const {
        data,
        isSuccess,
        isFetching,
        isError,
        error,
        refetch,
    } = useQuery<unknown, unknown, Type>({
        queryKey: [
            key,
            {
                ...params,
                token,
            },
        ],
        queryFn: requestFunc(dispatch),
        enabled,
        refetchOnWindowFocus: false,
        retry: false,
        staleTime,
        refetchInterval,
        refetchIntervalInBackground,
        placeholderData: keepPreviousData ? placeholderDataFnToKeepPreviousData : undefined,
    })

    const isDataReady = useMemo((): boolean => {
        return isSuccess && !isFetching
    }, [
        isSuccess,
        isFetching,
    ])

    const queryClient = useQueryClient()

    const invalidate = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: [key],
        })
    }, [
        queryClient,
        key,
    ])

    return {
        data,
        isDataReady,
        isFetching,
        isError,
        error,
        refetch,
        invalidate,
    }
}

export default useRequest
